import TextFieldWrapper, { TextFieldWrapperProps } from 'ComponentLibrary/src/atoms/forms/FormTextFields/TextFieldWrapper/TextFieldWrapper'
import React, { HTMLInputTypeAttribute } from 'react'
import { Control, Controller, FieldValues, Path, PathValue, RegisterOptions, FieldErrors } from 'react-hook-form'
import { get } from 'lodash'
import { BaseInput } from 'ComponentLibrary'
import { ReferencePlainEditor } from 'ComponentLibrary/src/molecules/TextEditor/DynamicReferences/ReferencePlainEditor'
import { useReferenceChoices } from 'ComponentLibrary/src/molecules/TextEditor/DynamicReferences/referenceModel'

type FormTextFieldProps<TFieldValues extends FieldValues> = {
    label?: string
    control: Control<TFieldValues>
    fieldName: Path<TFieldValues>
    defaultValue?: PathValue<TFieldValues, Path<TFieldValues>>
    className?: string
    errors: FieldErrors<TFieldValues>
    disabled?: boolean
    required?: boolean
    rules?:
        | Omit<RegisterOptions<TFieldValues, Path<TFieldValues>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled' | 'required'>
        | undefined
    type?: HTMLInputTypeAttribute
    onBlur?: () => void
    onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
    inputFieldStyles?: React.CSSProperties
    unit?: string
    hideLabel?: boolean
    multiline?: boolean
    maxRows?: string | number
    isDefaultValue?: boolean
    ariaLabeledBy?: string
    size?: 'small' | 'medium'
    textAlign?: 'left' | 'right' | 'center' | 'justify' | 'match-parent'
    resize?: 'none' | 'both' | 'horizontal' | 'vertical' | 'block' | 'inline'
    placeholder?: string
    shrinkLabel?: boolean
    gridStyles?: React.CSSProperties
    endAdornment?: React.ReactNode
    autoFocus?: boolean
    requiredMessage?: string
    enableReferences?: boolean
} & TextFieldWrapperProps

/**
 * @deprecated Use `FormTextInput` from the Piazza component library for new work.
 */
export default function FormTextField<TFieldValues extends FieldValues>(props: FormTextFieldProps<TFieldValues>) {
    const {
        label,
        control,
        fieldName,
        defaultValue,
        rules,
        className,
        onBlur,
        onFocus,
        onKeyDown,
        disabled = false,
        errors,
        fullWidth,
        required,
        type,
        inputFieldStyles,
        xs = 6,
        unit,
        hideLabel = false,
        multiline,
        maxRows,
        isDefaultValue,
        ariaLabeledBy,
        size = 'medium',
        textAlign,
        resize,
        placeholder,
        gridStyles,
        endAdornment,
        shrinkLabel,
        autoFocus,
        requiredMessage = 'Pflichtfeld',
        enableReferences = false,
    } = props
    const referenceChoices = useReferenceChoices()

    // Get is used to handle subfields like foo.bar.baz
    const error = get(errors, fieldName)
    const errorMessage = error?.message?.toString()
    const requiredRule = required ? { value: true, message: requiredMessage } : undefined

    return (
        <TextFieldWrapper fullWidth={fullWidth} xs={xs} label={label} required={required} hideLabel={hideLabel} styles={gridStyles}>
            <Controller
                control={control}
                defaultValue={defaultValue}
                name={fieldName}
                rules={{ ...rules, required: requiredRule }}
                render={({ field }) =>
                    enableReferences && multiline && referenceChoices ? (
                        <ReferencePlainEditor
                            value={String(field.value ?? '')}
                            inputRef={field.ref}
                            onChange={field.onChange}
                            onBlur={() => {
                                field.onBlur()
                                onBlur?.()
                            }}
                            onFocus={(event) => onFocus?.(event as unknown as React.FocusEvent<HTMLTextAreaElement>)}
                            onKeyDown={(event) => onKeyDown?.(event as unknown as React.KeyboardEvent<HTMLTextAreaElement>)}
                            choices={referenceChoices}
                            label={hideLabel || ariaLabeledBy ? undefined : label}
                            ariaLabel={label ?? fieldName}
                            ariaLabeledBy={ariaLabeledBy}
                            name={field.name}
                            placeholder={placeholder}
                            disabled={disabled}
                            required={required}
                            autoFocus={autoFocus}
                            errorMessage={errorMessage}
                            hasError={!!error}
                            size={size}
                            inputFieldStyles={inputFieldStyles}
                            maxRows={maxRows}
                            resize={resize || 'vertical'}
                            textAlign={textAlign}
                            className={className}
                            endAdornment={endAdornment}
                            unit={unit}
                            shrinkLabel={shrinkLabel}
                        />
                    ) : (
                        <BaseInput
                            {...field}
                            size={size}
                            textAlign={textAlign}
                            value={field.value !== undefined ? field.value : ''}
                            className={className}
                            label={label}
                            hasError={!!error}
                            errorMessage={errorMessage}
                            disabled={disabled}
                            required={required}
                            type={type}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            onKeyDown={onKeyDown}
                            autoFocus={autoFocus}
                            multiline={multiline}
                            maxRows={maxRows}
                            inputFieldStyles={inputFieldStyles}
                            ariaLabeledBy={ariaLabeledBy}
                            unit={unit}
                            isDefaultValue={isDefaultValue}
                            hideLabel={hideLabel}
                            resize={resize || (multiline ? 'vertical' : 'none')}
                            placeholder={placeholder}
                            endAdornment={endAdornment}
                            shrinkLabel={shrinkLabel}
                        />
                    )
                }
            />
        </TextFieldWrapper>
    )
}
